/**
 * Shared value comparison for the flattened offer/policy diffs.
 *
 * Both the offer diff behind staff-fe's "Report results" modal and policy-mod's
 * segment diffs flatten two records and report every key whose value moved. A lot
 * of those moves are not real: a field corrected to its empty state, an empty
 * state written back in a different shape, or a vendor spelling out an absence a
 * null already conveyed. This decides which pairs to ignore, and lives here so the
 * offer diff, the two policy-mod segment diffs and the modal all agree on what
 * "empty" means.
 */
import isEqual from 'lodash.isequal';

// Flattening leaves empty state in whatever shape the writer used, including the
// literal strings 'null' and 'undefined' from values that went through a template
// or a JSON round trip. Compared after trimming, so a whitespace-only value counts.
//
// Deliberately NOT here: the string '0'. It is a real value on fields whose lookup
// offers both "none" and "zero" as separate options — deductibleComprehensive has
// ids 'NONE' and '0' — so treating it as empty hid a $0 deductible appearing or
// disappearing from policy-mod's change list.
const EMPTY_STRINGS = ['', 'null', 'undefined'];

/**
 * Values that state an absence in words instead of by being empty. A move between
 * one of these and a genuinely empty value reads as "null to null" to a person, so
 * it is not a change worth reporting.
 *
 * Add an entry here as each new shape turns up, and say why it reads as nothing —
 * the point of the list is to grow.
 */
const nothingValuePatterns = [
  // Cape Analytics answers every presence question with `no_<thing>` / `with_<thing>`:
  // no_trampoline, no_pool, no_fence, no_solar_panel, no_roof_tarp, no_tree_overhang,
  // and so on. `trampolinePresence: null -> no_trampoline` tells a reader exactly what
  // the null already told them. The `with_<thing>` side is a real answer and is not
  // matched, so a move to or from it still shows up.
  /^no_[a-z][a-z_]*$/i
];

/**
 * True when a value spells out an absence rather than being empty — see
 * `nothingValuePatterns`. Split out from `isEmptyDiffValue` so a new pattern has one
 * obvious home, and so a caller can ask about this case on its own.
 */
export const isNothingValue = (value: any): boolean =>
  typeof value === 'string' && nothingValuePatterns.some((pattern) => pattern.test(value.trim()));

/**
 * True when a value is one of the empty states. Checked by type rather than by
 * coercing every candidate against every value: `0 == '000'` and `0 == '0x0'` are
 * both true in JavaScript, so a loose comparison quietly swept up zero-padded and
 * hex-shaped strings that are real values on some fields.
 */
export const isEmptyDiffValue = (value: any): boolean => {
  if (value === null || value === undefined || value === 0 || value === false) {
    return true;
  }
  if (typeof value === 'string') {
    return EMPTY_STRINGS.includes(value.trim()) || isNothingValue(value);
  }
  if (typeof value === 'object') {
    return isEqual(value, []) || isEqual(value, {});
  }
  return false;
};

/**
 * True when two values are equal, or when both are empty and so not worth
 * reporting as a change. Non-empty values fall through to a deep equality check.
 *
 * Every caller passes `flat()` output, whose leaves are scalars or an empty
 * array/object — never a populated container — so emptiness is a question about
 * the pair itself and needs no per-level customizer.
 */
export const isEqualIgnoringEmpty = (oldValue: any, newValue: any): boolean =>
  (isEmptyDiffValue(oldValue) && isEmptyDiffValue(newValue)) || isEqual(oldValue, newValue);

/**
 * Prior BI limits are stored in dollars (100000) but arrive in either unit, and
 * anything under 1,000 is taken to be in thousands already. The rater normalises
 * them this way before rating (`cd-auto-rater`) and so do the RC1 post-data rules,
 * so the rule lives here rather than in a third copy.
 *
 * Pure arithmetic on purpose: callers keep their own "is there a limit at all"
 * guard, and a non-numeric input yields NaN exactly as the inline versions did.
 */
export const priorBILimitToDollars = (limit: number | string): number => {
  const amount = Number(limit);
  return amount < 1000 ? amount * 1000 : amount;
};
